import {
  isBetween30And90DaysAhead,
  isLessThan30DaysAhead,
  isMoreThan12MonthsBehind,
  isMoreThan90DaysAhead,
} from '../helpers/date-comparisons';

export enum LicenceStatus {
  Full = 'F',
  Provisional = 'P',
}

export enum LicenceExpiryRiskLevel {
  High = 3,
  Medium = 2,
  Low = 1,
  None = 0,
}

export class DriverLicenceSummary {
  public readonly hasOldCheck: boolean = false;
  public readonly hasHighRiskLicenceStatus: boolean = false;
  public readonly licenceExpiryRiskLevel: number = LicenceExpiryRiskLevel.None;
  public readonly mandateExpiryRiskLevel: number = LicenceExpiryRiskLevel.None;

  public constructor(
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly pin: string,
    public readonly lastCheckDate: string | null,
    public readonly mandateExpiryDate: string | null,
    public readonly licenceExpiryDate: string | null,
    public readonly issueNumber: number | null,
    public readonly licenceStatus: string | null
  ) {
    this.hasOldCheck = this.lastCheckDate ? isMoreThan12MonthsBehind(this.lastCheckDate) : false;
    this.hasHighRiskLicenceStatus = this.determineLicenceStatusRiskLevel();
    this.licenceExpiryRiskLevel = this.determineExpiryRiskLevel(this.licenceExpiryDate);
    this.mandateExpiryRiskLevel = this.determineExpiryRiskLevel(this.mandateExpiryDate);
  }

  private determineLicenceStatusRiskLevel(): boolean {
    return !['FC (Current Full Licence)', 'Valid (Current Full Licence)'].includes(this.licenceStatus);
  }

  private determineExpiryRiskLevel(date?: string | null): number {
    if (!date) {
      return LicenceExpiryRiskLevel.None;
    }

    if (isMoreThan90DaysAhead(date)) {
      return LicenceExpiryRiskLevel.Low;
    }

    if (isBetween30And90DaysAhead(date)) {
      return LicenceExpiryRiskLevel.Medium;
    }

    if (isLessThan30DaysAhead(date)) {
      return LicenceExpiryRiskLevel.High;
    }

    return LicenceExpiryRiskLevel.None;
  }
}

export interface DriverLicenceSummaryApiResponse {
  data: {
    AGGridId: number;
    firstName: string;
    lastName: string;
    fullName: string;
    issueNumber: number | null;
    licenceExpiry: string | null;
    mandateExpires: string | null;
    receivedDate: string | null;
    referenceNumber: string;
    type: string | null;
  }[];
}

export interface DriverLicenceEndorsement {
  convictionDate: string | null;
  offenceDate: string | null;
  disqualificationExpiryDate: string | null;
  offenceCode: string | null;
  points: number | null;
  description: string | null;
}

export interface DriverLicenceEndorsementsApiResponse {
  data: {
    AGGridId: number;
    convictionDate: string | null;
    offenceDate: string | null;
    disqualEndDate: string | null;
    offenceCode: string | null;
    points: number | null;
    description: string | null;
  }[];
}

export interface DriverLicenceEntitlement {
  commenceDate: string | null;
  expiryDate: string | null;
  offenceCode: string | null;
  identifier: string | null;
  restrictionCodes: string | null;
  description: string | null;
}

export interface DriverLicenceEntitlementApiResponse {
  data: {
    AGGridId: number;
    commenceDate: string | null;
    expiryDate: string | null;
    code: string | null;
    identifier: string | null;
    restrictionCodes: string | null;
    description: string | null;
  }[];
}

export interface DriverLicenceRestriction {
  code: string | null;
  description: string | null;
}

export interface DriverLicenceRestrictionsApiResponse {
  data: {
    AGGridId: number;
    code: string | null;
    description: string | null;
  }[];
}
